import { FC, useEffect, useState } from 'react';
import { NudosButton } from '../../../../components/NudosComponents';
import BordOneToneIcon from '../../../../components/NudosComponents/BordOneToneIcon/BordOneToneIcon';
import { IMinDetails, IProductDelivery, IShipmentData, IproductReferenceDataInCountry, SHIPMENT_STATUS_ID } from '../../../../types/orders';
import { IMassiveDeliveryInProgress } from '../../../../types/traking';
import { updateToConfirmetToInWarehouse } from '../../../../services/traking.services';
import {
  displayErrorNotification,
  displaySuccessNotification
} from '../../../../utils/displayNudosStandardNotifications';
import ProductSelector from '../ProductSelector/ProductSelector';
import { IconReserveBox } from 'assets/DesignSystem/SVGComponents';
import { extractProductData } from 'utils/extractProductData';
import { FALLBACK_FLAG_IMAGE_URL } from '../../../../constants/constants';
import './AddSerialStopInWarehouseModal.scss';

interface AddSerialStopInWarehouseModalProps {
  orderId: string;
  setShowAddSerialStopInWarehouseModal: React.Dispatch<React.SetStateAction<boolean>>;
  selectedShipments?: IShipmentData[];
  products?: IProductDelivery[];
  shipmentData?: IShipmentData;
  updateWithoutLoaders?: () => void;
  organizationName?: string;
  references?: IproductReferenceDataInCountry[];
}

const AddSerialStopInWarehouseModal: FC<AddSerialStopInWarehouseModalProps> = ({
  orderId,
  setShowAddSerialStopInWarehouseModal,
  selectedShipments,
  products,
  shipmentData,
  updateWithoutLoaders,
  organizationName,
  references
}: AddSerialStopInWarehouseModalProps) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [listOfShipmentsToChangeStatus, setListOfShipmentsToChangeStatus] = useState<IShipmentData[]>([]);
  const [errorList, setErrorList] = useState<IMinDetails[] | undefined>([]);
  const [hasProductSelectorErrors, setHasProductSelectorErrors] = useState<boolean>(false);

  // Determinar si estamos en modo legacy (individual) o nuevo (masivo)
  const isLegacyMode = !selectedShipments && shipmentData;
  const shipmentsToUse = isLegacyMode && shipmentData ? [{ ...shipmentData, products: products || [] }] : selectedShipments?.filter(shipment => shipment?.statusId === SHIPMENT_STATUS_ID.CONFIRMED) || [];

  const isBulkAction = shipmentsToUse?.length > 1;
  const firstShipment = shipmentsToUse[0];

  const disableButton = () => {
    return hasProductSelectorErrors || listOfShipmentsToChangeStatus.length === 0;
  };

  

  const changeStatusToInWarehouse = async () => {
    if (loading) return;
    setLoading(true);
    try {
      const bodyDataArray: IMassiveDeliveryInProgress[] = listOfShipmentsToChangeStatus?.map(shipment => ({
        shipmentId: shipment?.shipmentId,
        newSerials: extractProductData(shipment?.products)
      }));

      const updateData: IMinDetails[] = await updateToConfirmetToInWarehouse(bodyDataArray);

      const errors = Array.isArray(updateData) ? updateData.filter(item => item?.error) : [];

      if (errors.length > 0) return setErrorList(errors);

      await updateWithoutLoaders?.();
      setShowAddSerialStopInWarehouseModal(false);

      displaySuccessNotification({
        customJSXMessage: isBulkAction ? (
          <>Las herramientas están en bodega. Ahora ve a WMS y escanéalas en Cross Docking.</>
        ) : (
          <>La herramienta está en bodega. Ahora ve a WMS y escanéala en Cross Docking.</>
        )
      });
    } catch (error) {
      displayErrorNotification();
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Inicializar con los shipments (masivo o individual)
    if (shipmentsToUse && shipmentsToUse?.length > 0) {
      setListOfShipmentsToChangeStatus(shipmentsToUse);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedShipments, products, shipmentData]);

  return (
    <div id="newModalAddSerialStopInWarehouse">
      <div className="containerForm">
        <div
          className="floatClose"
          onClick={() => {
            if (loading) return;
            setShowAddSerialStopInWarehouseModal(false);
          }}
        >
          <BordOneToneIcon variant="x" customWidth="16px" />
        </div>
        <div className="boxForm">
          <div className="titleModal">
            <div className="iconContainer">
              <IconReserveBox />
            </div>
            De Confirmado a En bodega
          </div>
          <div className="containerSubTitle">
            <span className="firstPartSubTitle">{`${organizationName || 'Nombre Empresa'} - Orden N°${
              orderId || 1
            }`}</span>
            {!isBulkAction && (
              <>
                <span>|</span>
                <span>{`Envío - ${firstShipment?.receiverInfo?.receiverName ?? ''}`}</span>
                <img
                  src={firstShipment?.country?.flag || FALLBACK_FLAG_IMAGE_URL}
                  width={14}
                  alt="warehouse-modal-flag"
                />
              </>
            )}
            {isBulkAction && (
              <>
                <span>|</span>
                <span>{`${shipmentsToUse?.length} envíos seleccionados`}</span>
              </>
            )}
          </div>
          <div className="containerDetails">
            <div className="containerProducts">
              <ProductSelector
                headerTitle="Selecciona las herramientas que pasan a En bodega"
                {...(isLegacyMode 
                  ? { shippingChosenByDefault: shipmentsToUse[0] } 
                  : { listOfSelectedShipments: shipmentsToUse }
                )}
                setListOfShipmentsToChangeStatus={setListOfShipmentsToChangeStatus}
                displayTemporaryStorageAlert
                customAlertMessage={
                  isBulkAction
                    ? 'Al confirmar, las herramientas aparecerán en WMS y quedarán Por escanear en Cross Docking.'
                    : 'Al confirmar, la herramienta aparecerá en WMS y quedará Por escanear en Cross Docking.'
                }
                hasTemporaryStorage
                errorList={errorList}
                setErrorList={setErrorList}
                serialHasAnyError={hasErrors => setHasProductSelectorErrors(hasErrors)}
                requireSerials
                references={references}
              />
            </div>
          </div>
          <div className="buttonContainer">
            <NudosButton
              componentSize="large"
              colorPalette="primary"
              buttonText="Guardar"
              isButtonDisabled={disableButton()}
              handleClick={changeStatusToInWarehouse}
              isButtonLoading={loading}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddSerialStopInWarehouseModal;
